import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import IconButton from '@/storybook/components/IconButton/IconButton';
import Text from '@/storybook/components/Text/Text';
import InfoCircleIcon from '@/storybook/icons/InfoCircleIcon';
import TooltipWrapper from '@/components/base/TooltipWrapper/TooltipWrapper';
import useChatbotStore from '../../store/useChatbotStore';
import { getRateLimitSeverity, formatTimeRemaining, normalizeRateLimit } from './rateLimit.utils';
import { RATE_LIMIT_STATUS_TEST_IDS } from './testIds';
import styles from './RateLimitStatus.module.css';

const TOOLTIP_ID = 'rate-limit-info-tooltip';

const RateLimitStatus: FC = () => {
  const { t } = useTranslation();
  const rateLimit = useChatbotStore(s => normalizeRateLimit(s.rateLimit));

  const { remaining, limit, resetAt } = rateLimit;

  if (remaining === null || limit === null) return null;

  const severity = getRateLimitSeverity(remaining);
  const timeLeft = resetAt !== null ? formatTimeRemaining(resetAt) : null;

  const statusText = timeLeft
    ? t('components.aiChatDrawer.rateLimit.status', { remaining, timeLeft })
    : t('components.aiChatDrawer.rateLimit.statusNoTime', { remaining });

  const tooltipText = timeLeft
    ? t('components.aiChatDrawer.rateLimit.tooltipInfo', { limit, timeLeft })
    : t('components.aiChatDrawer.rateLimit.tooltipInfoNoTime', { limit });
  const tooltipLines = [tooltipText];
  if (severity === 'exhausted') {
    tooltipLines.push(t('components.aiChatDrawer.rateLimit.tooltipExhausted'));
  }
  const tooltipContent = tooltipLines.join(' ');

  return (
    <div className={cx(styles.statusLine, styles[severity])} data-testid={RATE_LIMIT_STATUS_TEST_IDS.rateLimitStatus}>
      <Text as="span">{statusText}</Text>
      <IconButton
        appearance="text"
        size="S"
        icon={<InfoCircleIcon width={18} height={18} />}
        className={styles.infoButton}
        data-tooltip-id={TOOLTIP_ID}
        aria-label={t('components.aiChatDrawer.rateLimit.infoAriaLabel')}
        data-testid={RATE_LIMIT_STATUS_TEST_IDS.rateLimitInfoButton}
      />
      <TooltipWrapper
        id={TOOLTIP_ID}
        place="top"
        variant="dark"
        className={styles.tooltip}
        data-testid={RATE_LIMIT_STATUS_TEST_IDS.rateLimitTooltip}
      >
        {tooltipContent}
      </TooltipWrapper>
    </div>
  );
};

export default RateLimitStatus;
